import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMoreHorizontal, FiFilter } from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import { mockCandidates, pipelineStages, mockRoles } from '../data/mockData';
import CandidateModal from '../components/CandidateModal';

const PipelineView = () => {
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [activeRole, setActiveRole] = useState(mockRoles[0].id);

  const filteredCandidates = mockCandidates.filter(c => c.role === activeRole);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400 border-green-400/30 bg-green-400/10';
    if (score >= 70) return 'text-blue-400 border-blue-400/30 bg-blue-400/10';
    return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <span className="w-2 h-8 bg-green-500 rounded-full shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
            Talent Radar
          </h1>
          <p className="text-slate-400 text-sm mt-1">Drag candidates across lanes or click to view scorecard.</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={activeRole}
            onChange={(e) => setActiveRole(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            {mockRoles.map(role => (
              <option key={role.id} value={role.id}>{role.title}</option>
            ))}
          </select>
          <button className="p-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiFilter} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max h-full">
          {pipelineStages.map(stage => {
            const stageCandidates = filteredCandidates.filter(c => c.stage === stage);
            
            return (
              <div key={stage} className="w-80 flex flex-col bg-slate-900/50 rounded-xl border border-slate-800/50 overflow-hidden">
                <div className="p-4 border-b border-slate-800 bg-slate-900 flex justify-between items-center sticky top-0">
                  <h3 className="font-semibold text-slate-300 uppercase tracking-widest text-xs">{stage}</h3>
                  <span className="bg-slate-800 text-slate-400 text-xs px-2 py-0.5 rounded-full font-medium">
                    {stageCandidates.length}
                  </span>
                </div>
                
                <div className="flex-1 p-3 space-y-3 overflow-y-auto custom-scrollbar">
                  {stageCandidates.map(candidate => (
                    <motion.div
                      key={candidate.id}
                      layoutId={`card-${candidate.id}`}
                      onClick={() => setSelectedCandidate(candidate)}
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="bg-slate-800 border border-slate-700 p-4 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors shadow-lg relative group"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-slate-200 text-sm">{candidate.name}</h4>
                        <button className="text-slate-500 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
                          <SafeIcon icon={FiMoreHorizontal} />
                        </button>
                      </div>
                      <div className="flex justify-between items-end mt-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Status</span>
                          <span className="text-xs text-slate-300">{candidate.status}</span>
                        </div>
                        <div className={`px-2 py-1 rounded text-xs font-bold border ${getScoreColor(candidate.score)}`}>
                          {candidate.score}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  {stageCandidates.length === 0 && (
                    <div className="h-24 border-2 border-dashed border-slate-800 rounded-lg flex items-center justify-center text-slate-600 text-sm">
                      Drop candidate here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedCandidate && (
        <CandidateModal 
          candidate={selectedCandidate} 
          onClose={() => setSelectedCandidate(null)} 
        />
      )}
    </div>
  );
};

export default PipelineView;